/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent, ProgressBarComponent } from '../../../../../core/components';
import { ClusterService, NodeModel, SupportApiService } from '../../../shared';
import { Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { DownloadService, LoadingService, LogService, NotifyService, RbacService } from '../../../../../core/services';
import { ErrorResponseModel } from '../../../../../core/models';
import { WidgetService } from '../../../shared/services/utilities/widget.service';
import { WidgetStateEnum } from '../../../../../core/enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-support',
    templateUrl: './technicalSupportDownload.component.html',
    styleUrls: ['./technicalSupportDownload.component.scss']
})

export class TechnicalSupportDownloadComponent extends BaseComponent implements OnInit {
    /**
     * Download progress container.
     */
    @ViewChild('progressContainer', { read: ViewContainerRef })
    public progressContainer: any;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the created component.
     */
    private componentReference: ComponentRef<any>;

    /**
     * To store the recommended download state.
     */
    private state: WidgetStateEnum;

    constructor(
        private clusterService: ClusterService,
        private downloadService: DownloadService,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private supportApiService: SupportApiService,
        private widgetService: WidgetService
    ) {
        super();
        this.registerButtons();
    }

    public ngOnInit(): void {
        this.subscribeToSocketData();
    }

    /**
     * Request the recommended support download file.
     */
    public requestRecommendedFileDownload(): void {
        const size: string = this.getDownloadSize();
        this.triggerDownload(size);
    }

    /**
     * Download the recommended download file based on the appliance state.
     * @private
     */
    private getDownloadSize(): string {
        let size: string = 'small';
        switch (this.state) {
            case WidgetStateEnum.CRITICAL:
                size = 'small';
                break;
            case WidgetStateEnum.WARNING:
                size = 'medium';
                break;
            case WidgetStateEnum.OK:
                size = 'large';
                break;
        }

        return size;
    }

    /**
     * Downloaded the support file.
     * @param size
     * @private
     */
    private triggerDownload(size: string): void {
        this.showProgressBar();
        this.supportApiService.getDownload(size)
            .subscribe({
                next: (data: any): void => {
                    this.cleanUpProgressComponent();
                    if (data['downloadData']) {
                        this.createSmallDownloadFile(data);
                    } else {
                        this.showProgressBar(data['filename']);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.notifyService.error('support_create_support_archive_error', {
                        id: 'create-support-archive-error',
                        system: true,
                        messageTranslate: true
                    });
                    this.logService.error('SupportApiService.GetDownload()', 'Failed to create support archive.', error);
                }
            });
    }

    /**
     * Create small download file.
     * @param data
     * @private
     */
    private createSmallDownloadFile(data: any): void {
        const jsonData: string = atob(data['downloadData']);
        const file = this.convertStringToBytes(jsonData);
        this.downloadService.generateDownload(file, data['filename'], { type: 'application/zip' });
        this.downloadComplete();
    }

    /**
     * Convert string to bytes.
     * @param str
     * @returns {Uint8Array}
     */
    private convertStringToBytes(str: string): any {
        const bytes: Uint8Array = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) {
            bytes[i] = str.charCodeAt(i);
        }

        return bytes;
    }

    /**
     * Show the progress bar in the footer.
     * @param filename
     * @private
     */
    private showProgressBar(filename?: string): void {
        const progressComponent = this.progressContainer.createComponent(ProgressBarComponent);
        // Set value in the progress component.
        if (filename) {
            const localNode: NodeModel = this.clusterService.localNode;
            progressComponent.instance.fileUrl =
                `https://${localNode.ip}/api/v1/${localNode.id}/support/download/file/${filename}`;
        } else {
            progressComponent.instance.showPercentage = false;
            progressComponent.instance.timedProgressBarTime = 300;
            progressComponent.instance.progressPrefixText = 'support_technical_create_support_archive';
        }

        progressComponent.instance.spinner = true;
        progressComponent.instance.onCompleteCallBack = this.cleanUpHelper.bind(this);
        // Component reference for destroy later.
        this.componentReference = progressComponent;
    }

    /**
     * Call both clean up functions on complete
     * @private
     */
    private cleanUpHelper(): void {
        this.downloadComplete();
        this.cleanUpProgressComponent();
    }

    /**
     * When the download is complete, stop the loading button and show user success message
     * @private
     */
    private downloadComplete(): void {
        this.loadingService.loadingButtonEmitter.next(false);
        this.notifyService.success('support_technical_archive_success', { messageTranslate: true });
    }

    /**
     * Clean up progress component.
     * @private
     */
    private cleanUpProgressComponent(): void {
        if (this.componentReference) {
            this.componentReference.destroy();
        }
    }

    /**
     * Set up socket listener to determine the appliance state for recommended download file.
     * @private
     */
    private subscribeToSocketData(): void {
        let ram: any;
        let disk: any;
        this.widgetService.statusDataEmitter.pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    const disksValue = Object.values(response.disks)[0];
                    const memoryValue = Object.values(response.memory)[0];

                    if (JSON.stringify(disk) !== JSON.stringify(disksValue) && JSON.stringify(ram) !== JSON.stringify(memoryValue)) {
                        disk = disksValue;
                        ram = memoryValue;

                        const diskParts = Object.values(disk)[0];
                        const totalDisk = diskParts['used'] + diskParts['free'];
                        const percentageDisk: number = Math.round((diskParts['used'] / totalDisk) * 100);
                        const totalMemory = memoryValue['used'] + memoryValue['free'];
                        const percentageMemory: number = Math.round((memoryValue['used'] / totalMemory) * 100);
                        this.state = this.getState(percentageDisk, percentageMemory);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('TechnicalSupportDownloadComponent:socketListeners() error.', error.message);
                }
            });

        this.widgetService.sendSubscriptionConfirmEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (service): void => {
                    if (service === 'systemStats') {
                        this.widgetService.receiveSubscriptionConfirmEmitter.next({
                            service: 'systemStats',
                            event: 'stats'
                        });
                    }
                }
            });
    }

    /**
     * Get the appliance state based on available disk and ram to
     * determine which tsd to recommend for downloading.
     * @param disk
     * @param ram
     * @private
     */
    private getState(disk: number, ram: number): any {
        if (disk >= 90 && ram >= 50) {
            return WidgetStateEnum.CRITICAL;
        } else if (ram >= 50 || disk >= 70 && disk < 90) {
            return WidgetStateEnum.WARNING;
        } else {
            return WidgetStateEnum.OK;
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'download': [
                { path: '/support/download/:option', methods: ['GET'] },
                { path: '/support/download/file/:filename', methods: ['GET'] }
            ]
        };
        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
